import axios from '@/lib/axios';
import type { 
  SubscriptionPlan, 
  EnterpriseContactData, 
  UsageMetrics,
  UsageRecord,
  PlanChangeResult 
} from '@/types/plan';

export interface CurrentPlanResponse {
  plan: SubscriptionPlan;
  usage: UsageMetrics;
  /**
   * Whether the current user can manage (upgrade/downgrade) the shared account plan.
   * Only OWNER/GERENTE/ADMIN can manage it (account-level-subscription, Req 4.6, 7.1).
   */
  canManagePlan: boolean;
  /**
   * Number of account members the plan is shared with (Req 8.5, 7.1).
   */
  sharedWith: number;
}

export const plansApi = {
  getAllPlans: async (): Promise<SubscriptionPlan[]> => {
    const response = await axios.get('/plans');
    return response.data;
  },

  getPlanByCode: async (code: string): Promise<SubscriptionPlan> => {
    const response = await axios.get(`/plans/${code}`);
    return response.data;
  },

  getCurrentPlan: async (): Promise<CurrentPlanResponse> => {
    const response = await axios.get('/plans/current');
    const data = response.data;
    return {
      plan: {
        id: data.id || '',
        planCode: data.planCode,
        name: data.name,
        description: data.description,
        maxPublicIntegration: data.maxPublicIntegration,
        dailyEventsLimit: data.dailyEventsLimit,
        monthlyEventsLimit: data.monthlyEventsLimit,
        monthlyPrice: data.monthlyPrice,
        overagePrice: data.overagePrice,
        supportEnabled: data.supportEnabled,
        retryEnabled: data.retryEnabled,
        isActive: data.isActive,
        displayOrder: data.displayOrder,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
      },
      usage: data.currentUsage || {
        publicFeedsCount: 0,
        privateFeedsCount: 0,
        eventsProcessed: 0,
        dailyEventsProcessed: 0,
        overageEvents: 0,
        overageCharges: 0,
        baseCharge: 0,
        totalCharge: 0,
        month: '',
        usageResetDate: '',
      },
      // Account-level metadata (account-level-subscription). Defaults keep
      // backward compatibility when the backend omits the fields.
      canManagePlan: data.canManagePlan ?? true,
      sharedWith: data.sharedWith ?? 1,
    };
  },

  getCurrentUsage: async (): Promise<UsageMetrics> => {
    const response = await axios.get('/plans/usage');
    return response.data;
  },

  getUsageHistory: async (months: number = 6): Promise<UsageRecord[]> => {
    const response = await axios.get('/plans/usage/history', {
      params: { months }
    });
    return response.data;
  },

  upgradePlan: async (planCode: string): Promise<SubscriptionPlan> => {
    const response = await axios.post('/plans/upgrade', { planCode });
    return response.data;
  },

  downgradePlan: async (planCode: string): Promise<PlanChangeResult> => {
    const response = await axios.post('/plans/downgrade', { planCode });
    return response.data;
  },

  submitEnterpriseContact: async (data: EnterpriseContactData): Promise<void> => {
    await axios.post('/plans/enterprise/contact', data);
  },
};
